import { useEffect, useState } from "react";
import type { RsvpCaps, Rsvp, RsvpStatus } from "@firsttoast/shared";
import { UNLIMITED_HARD_MAX, validateRsvp } from "@firsttoast/shared";
import { submitRsvp } from "../api.js";

interface Props {
  token: string;
  caps: RsvpCaps;
  existing?: Rsvp;
  onSaved: (rsvp: Rsvp) => void;
}

function range(max: number): number[] {
  return Array.from({ length: max + 1 }, (_, i) => i);
}

// A capped count renders as a dropdown; "unlimited" renders as a number input.
function CountField({
  label,
  cap,
  value,
  onChange,
}: {
  label: string;
  cap: RsvpCaps["adults"];
  value: number;
  onChange: (n: number) => void;
}) {
  // Internal string so the user can clear the field mid-edit without it
  // snapping back to 0 on every keystroke.
  const [raw, setRaw] = useState(String(value));
  useEffect(() => { setRaw(String(value)); }, [value]);

  function commit(str: string) {
    const n = parseInt(str, 10);
    const clamped = isNaN(n) ? 0 : Math.max(0, Math.min(UNLIMITED_HARD_MAX, n));
    setRaw(String(clamped));
    onChange(clamped);
  }

  return (
    <label className="flex flex-1 flex-col gap-1">
      <span className="text-sm text-muted">{label}</span>
      {cap === "unlimited" ? (
        <input
          type="number"
          min={0}
          max={UNLIMITED_HARD_MAX}
          className="field"
          value={raw}
          onFocus={(e) => e.target.select()}
          onChange={(e) => {
            setRaw(e.target.value);
            const n = parseInt(e.target.value, 10);
            if (!isNaN(n)) onChange(Math.max(0, Math.min(UNLIMITED_HARD_MAX, n)));
          }}
          onBlur={(e) => commit(e.target.value)}
        />
      ) : (
        <select className="field" value={value} onChange={(e) => onChange(Number(e.target.value))}>
          {range(cap).map((n) => (
            <option key={n} value={n}>{n}</option>
          ))}
        </select>
      )}
    </label>
  );
}

export function RsvpForm({ token, caps, existing, onSaved }: Props) {
  const [status, setStatus] = useState<RsvpStatus>(existing?.status ?? "yes");
  const [adults, setAdults] = useState(existing?.adults ?? 1);
  const [kids, setKids] = useState(existing?.kids ?? 0);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const attending = status === "yes";

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    const check = validateRsvp({ status, adults, kids }, caps);
    if (!check.ok) {
      setError(check.error);
      return;
    }

    setSaving(true);
    try {
      const res = await submitRsvp(token, check.value);
      if (res.rsvp) onSaved(res.rsvp);
      else setError("Could not save your response. Please try again.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong.");
    } finally {
      setSaving(false);
    }
  }

  const choiceClass = (active: boolean) =>
    `flex-1 cursor-pointer rounded-xl border p-4 text-center transition ${
      active
        ? "border-accent bg-accent/10"
        : "border-accent/20 bg-cream/70 hover:bg-cream"
    }`;

  return (
    <form className="card" onSubmit={onSubmit}>
      <h2 className="text-xl font-semibold">Will you join us?</h2>

      <div className="mt-4 flex flex-col gap-3 sm:flex-row">
        <label className={choiceClass(attending)}>
          <input
            type="radio"
            name="status"
            className="mb-1 block accent-accent"
            checked={attending}
            onChange={() => setStatus("yes")}
          />
          Joyfully accept
        </label>
        <label className={choiceClass(!attending)}>
          <input
            type="radio"
            name="status"
            className="mb-1 block accent-accent"
            checked={!attending}
            onChange={() => setStatus("no")}
          />
          Regretfully decline
        </label>
      </div>

      {attending && (
        <div className="mt-4 flex gap-4">
          <CountField label="Adults (including you)" cap={caps.adults} value={adults} onChange={setAdults} />
          <CountField label="Kids (5–18)" cap={caps.kids} value={kids} onChange={setKids} />
        </div>
      )}

      {error && <p className="mt-3 text-red-700">{error}</p>}

      <button className="btn-primary mt-5 w-full" type="submit" disabled={saving}>
        {saving ? "Saving…" : existing ? "Update response" : "Send RSVP"}
      </button>
    </form>
  );
}
